// react
import { useMemo, useState } from 'react';

// component
import { ChipProps } from '@/components/atoms/Chip/Chip';
import { LoadingTransition } from '@/components/atoms/LoadingTransition/LoadingTransition';
import { QuickResult, Search } from '@/components/atoms/Search/Search';
import { TooltipOnboarding } from '@/components/atoms/TooltipOnboarding/TooltipOnboarding';
import { FilterChips } from '@/pwa/molecules/FilterChips/FilterChips';
import { ArticleFiltered } from '@/components/organisms/ArticleFiltered/ArticleFiltered';
import { ArticleSummary } from '@/components/organisms/ArticleSummary/ArticleSummary';
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';

// store
import { Article, ArticleCategory } from '@karehero/models';

// mui
import { Box, Stack, Typography } from '@mui/material';

const onboardingSteps = 2;

type DummyArticle = {
  title: string;
};

type DummyArticleCategory = {
  title: string;
  articles: DummyArticle[];
};

/**
 * ArticleOverview is a page that displays all of the content that available
 * to the user.
 */
export const ArticleOverview = ({
  library,
  bookmarkedArticles,
  onBookmark,
  isLoading,
  searchValue,
  onSearchChange,
  isSearchLoading,
  quickResultsHeader,
  quickResults,
  filterChips,
  filteredArticles,
  isFilteredLoading,
  isOnboarding,
  toBookmarks,
  onOnboardingComplete,
  onArticleClick,
  onBack,
}: ArticleOverviewProps) => {
  // state
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [onboardingIndex, setOnboardingIndex] = useState(isOnboarding ? 0 : -1);

  const dummyArticles = useMemo(() => {
    let dummyArticles: DummyArticle[] = [];
    for (let i = 0; i < 4; i++) {
      dummyArticles.push({ title: `Article ${i}` });
    }
    return dummyArticles;
  }, []);

  // memos
  const dummyLibrary = useMemo(() => {
    let dummyLibrary: DummyArticleCategory[] = [];
    for (let i = 0; i < 2; i++) {
      dummyLibrary.push({
        title: `Category ${i}`,
        articles: dummyArticles,
      });
    }
    return dummyLibrary;
  }, [dummyArticles]);

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        maxWidth='md'
        headerSlot={
          <TooltipOnboarding
            index={0}
            title='Searching'
            content='Explore the library by using the search bar and filters'
            total={onboardingSteps}
            current={onboardingIndex}
            setCurrent={setOnboardingIndex}
            onComplete={onOnboardingComplete}
          >
            <Stack
              sx={{
                gap: 2,
              }}
            >
              <Typography variant='h5'>Library</Typography>
              <Typography
                variant='body1'
                sx={{
                  color: 'text.secondary',
                }}
              >
                This is where you'll find useful resources, interesting read and
                useful videos.
              </Typography>
              <Search
                isOpen={isSearchOpen}
                setIsOpen={setIsSearchOpen}
                value={searchValue}
                isLoading={isSearchLoading}
                onChange={onSearchChange}
                quickResultsHeader={quickResultsHeader}
                quickResults={quickResults}
              />
              <Box>
                <FilterChips
                  activeIds={(filterChips ?? [])
                    .filter((c) => c.isSelected)
                    .map((c) => c.label)}
                  items={[
                    ...(filterChips ?? []).map((c) => ({
                      id: c.label,
                      label: c.label,
                      to: c.to,
                      replace: c.replace,
                      icon: c.icon,
                    })),
                    {
                      id: 'bookmarks',
                      label: 'Bookmarks',
                      icon: 'bookmark' as const,
                      to: toBookmarks || '/',
                    },
                  ]}
                />
              </Box>
            </Stack>
          </TooltipOnboarding>
        }
        contentSlot={
          <LoadingTransition
            isLoading={isLoading}
            loading={
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {isFilteredLoading ? (
                  <ArticleFiltered articles={dummyArticles} isLoading />
                ) : (
                  <ArticleSummary library={dummyLibrary} isLoading />
                )}
              </Box>
            }
            content={
              <>
                {filteredArticles ? (
                  <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <ArticleFiltered
                      articles={filteredArticles}
                      bookmarkedArticles={bookmarkedArticles}
                      onBookmark={onBookmark}
                      onArticleClick={onArticleClick}
                      isLoading={false}
                    />
                  </Box>
                ) : (
                  <TooltipOnboarding
                    index={1}
                    title='Discover'
                    content='Find articles that may be of interest to you'
                    total={onboardingSteps}
                    current={onboardingIndex}
                    setCurrent={setOnboardingIndex}
                    onComplete={onOnboardingComplete}
                  >
                    <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <ArticleSummary
                        library={library}
                        bookmarkedArticles={bookmarkedArticles}
                        onBookmark={onBookmark}
                        onArticleClick={onArticleClick}
                        isLoading={false}
                      />
                    </Box>
                  </TooltipOnboarding>
                )}
              </>
            }
          />
        }
      />
    </ModalPageTemplate>
  );
};

interface ArticleOverviewProps {
  library?: ArticleCategory[];
  bookmarkedArticles?: Article[];
  onBookmark?: (article: Partial<Article>) => void;
  isLoading?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  isSearchLoading?: boolean;
  quickResultsHeader?: string;
  quickResults?: QuickResult[];
  filterChips?: ChipProps[];
  filteredArticles?: Article[];
  isFilteredLoading?: boolean;
  isOnboarding?: boolean;
  onOnboardingComplete?: () => void;
  onArticleClick?: (article?: Partial<Article>) => void;
  toBookmarks?: string;
  onBack?: () => void;
}

ArticleOverview.displayName = 'ArticleOverview';

export default ArticleOverview;
